import type { ReactNode } from "react";

import { Button, type ButtonProps } from "@/components/ui/Button";
import { HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { hubSaveButtonDisabled, resolveHubSaveHoverHint } from "@/components/ui/hubSaveButtonLogic";

export { HUB_SAVE_NO_CHANGES_HINT } from "@/components/ui/hubSaveButtonLogic";

type HubSaveButtonProps = Omit<ButtonProps, "disabled" | "children"> & {
  /** True when the operator has unsaved edits relative to the last persisted snapshot. */
  dirty: boolean;
  /**
   * Why the button is blocked besides a clean form (read-only, missing permission).
   * Shown instead of the no-changes hint when the extra `disabled` flag is set.
   */
  blockedHint?: string | null;
  /**
   * Optional plain-text summary of what differs from the last saved snapshot.
   * Shown on hover when the button is enabled (dirty) so operators can see why Save is active.
   */
  dirtyHint?: string | null;
  /** Additional disable reasons (validation, missing selection). Combined with !dirty. */
  disabled?: boolean;
  children?: ReactNode;
  layout?: "inline" | "block";
};

/**
 * Explicit persist control: disabled until the form is dirty, with a hover hint that
 * explains why. Use for Save / Save changes on existing records — not Create, Save as new,
 * or deferred-commit field glyphs.
 */
export function HubSaveButton({
  dirty,
  blockedHint,
  dirtyHint,
  disabled,
  isLoading,
  children = "Save",
  layout = "inline",
  type = "button",
  ...rest
}: HubSaveButtonProps): JSX.Element {
  const isPending = isLoading === true;
  const extraDisabled = disabled === true;
  const isDisabled = hubSaveButtonDisabled({ dirty, extraDisabled, isPending });
  const hoverHint = resolveHubSaveHoverHint({
    dirty,
    isPending,
    blockedHint: extraDisabled ? blockedHint : null,
    dirtyHint: dirty ? dirtyHint : null,
  });

  const button = (
    <Button type={type} {...rest} disabled={isDisabled} isLoading={isLoading}>
      {children}
    </Button>
  );

  if (!isDisabled && hoverHint != null) {
    return (
      <HoverHintTrigger
        trigger={button}
        align="end"
        className={layout === "block" ? "w-full" : undefined}
        block={layout === "block"}
      >
        <span className="whitespace-pre-wrap">{hoverHint}</span>
      </HoverHintTrigger>
    );
  }

  return (
    <ReadOnlyHoverLayer
      active={isDisabled && hoverHint != null}
      hoverHint={hoverHint ?? ""}
      layout={layout}
    >
      {button}
    </ReadOnlyHoverLayer>
  );
}

export type { HubSaveButtonProps };
